// ═══════════════════════════════════════════════════════════════════════════
// Web Push — the backend has had full send infrastructure since before this
// file existed (PushNotificationService fires on real transaction/card
// events via minishlink/web-push; /client/push-subscribe and
// /client/push-unsubscribe already existed) but nothing on the client ever
// registered a service worker or asked the browser to subscribe. This is
// the missing half: turn a user's "on" toggle into a real browser
// subscription, so a real push actually reaches their device.
// ═══════════════════════════════════════════════════════════════════════════
import { ADMIN_API, bearerHeaders } from "./api"

export function isPushSupported(): boolean {
    return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
}

/** 'granted' | 'denied' | 'default' (never asked yet) — 'denied' is
 *  permanent until the user changes it in their own browser/OS settings;
 *  there is no programmatic way back from it, so callers should show that
 *  state as "blocked", not offer a retry button that will silently no-op. */
export function notificationPermission(): NotificationPermission | "unsupported" {
    if (!isPushSupported()) return "unsupported"
    return Notification.permission
}

async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    try {
        return await navigator.serviceWorker.register("/sw.js")
    } catch (_e) {
        return null
    }
}

/** VAPID public keys are base64url, but SubscriptionOptions.applicationServerKey
 *  wants raw bytes — this is the standard conversion every Web Push
 *  integration needs, ported from the canonical MDN/web.dev recipe. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const raw = atob(base64)
    const bytes = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
    return bytes
}

let cachedVapidKey: string | null = null
async function fetchVapidPublicKey(): Promise<string | null> {
    if (cachedVapidKey) return cachedVapidKey
    try {
        const r = await fetch(`${ADMIN_API}/client/push-public-key`)
        if (!r.ok) return null
        const data = await r.json()
        if (typeof data.public_key === "string" && data.public_key) {
            cachedVapidKey = data.public_key
            return cachedVapidKey
        }
        return null
    } catch (_e) {
        return null
    }
}

/** True if THIS browser currently holds a push subscription — the honest
 *  source of truth for the toggle's initial state (not a localStorage
 *  flag, which could say "on" after the user cleared site data or
 *  uninstalled the PWA and the subscription is long gone). */
export async function isCurrentlySubscribed(): Promise<boolean> {
    if (!isPushSupported()) return false
    try {
        const reg = await navigator.serviceWorker.getRegistration("/sw.js")
        if (!reg) return false
        const sub = await reg.pushManager.getSubscription()
        return !!sub
    } catch (_e) {
        return false
    }
}

export type PushEnableResult = "enabled" | "unsupported" | "denied" | "error"

/** Full turn-on sequence: register the service worker, ask for OS
 *  permission (the one truly irreversible-by-code step — a "no" here is a
 *  "no" until the user changes it themselves), subscribe with the
 *  browser's push service, then tell the backend where to actually send
 *  to. Every failure mode returns a distinct reason rather than a bare
 *  false, so the UI can say something honest instead of a generic error. */
export async function enablePush(accessToken: string): Promise<PushEnableResult> {
    if (!isPushSupported()) return "unsupported"

    if (Notification.permission === "denied") return "denied"

    const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission()
    if (permission !== "granted") return "denied"

    const reg = await registerServiceWorker()
    if (!reg) return "error"

    const vapidKey = await fetchVapidPublicKey()
    if (!vapidKey) return "error"

    try {
        let sub = await reg.pushManager.getSubscription()
        if (!sub) {
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidKey),
            })
        }

        const json = sub.toJSON()
        const r = await fetch(`${ADMIN_API}/client/push-subscribe`, {
            method: "POST",
            headers: bearerHeaders(accessToken),
            body: JSON.stringify({
                endpoint: sub.endpoint,
                p256dh: json.keys?.p256dh,
                auth: json.keys?.auth,
            }),
        })
        if (!r.ok) return "error"
        return "enabled"
    } catch (_e) {
        return "error"
    }
}

export async function disablePush(accessToken: string): Promise<boolean> {
    if (!isPushSupported()) return true
    try {
        const reg = await navigator.serviceWorker.getRegistration("/sw.js")
        const sub = await reg?.pushManager.getSubscription()
        if (!sub) return true

        const endpoint = sub.endpoint
        await sub.unsubscribe()
        await fetch(`${ADMIN_API}/client/push-unsubscribe`, {
            method: "POST",
            headers: bearerHeaders(accessToken),
            body: JSON.stringify({ endpoint }),
        }).catch(() => {})
        return true
    } catch (_e) {
        return false
    }
}

/** Clears the app-icon badge the service worker sets on an incoming push.
 *  Called when the user actually looks at their notifications, so the
 *  badge means "something you haven't seen yet", not "something arrived
 *  at some point". Safe no-op where the Badging API doesn't exist. */
export function clearAppBadge(): void {
    try {
        ; (navigator as any).clearAppBadge?.()
    } catch (_e) {}
}
